<template>
  <div>
    <b-card class="mb-2">
      <b-card-text>
        <b-row class="align-items-end">
          <b-col
            md="2"
            cols="12"
            class="mb-2 mb-md-0"
          >
            <b-form-group
              :label="$t('accounting.doctors-revenue-paid-from')"
              class="mb-md-0"
            >
              <datepicker
                v-model="filters.paid_at_from"
                :calendar-button="true"
                :clear-button="true"
                :bootstrap-styling="true"
                placeholder="YYYY-MM-DD"
              />
            </b-form-group>
          </b-col>

          <b-col
            md="2"
            cols="12"
            class="mb-2 mb-md-0"
          >
            <b-form-group
              :label="$t('accounting.doctors-revenue-paid-to')"
              class="mb-md-0"
            >
              <datepicker
                v-model="filters.paid_at_to"
                :calendar-button="true"
                :clear-button="true"
                :bootstrap-styling="true"
                placeholder="YYYY-MM-DD"
              />
            </b-form-group>
          </b-col>

          <b-col
            md="2"
            cols="12"
            class="mb-2 mb-md-0"
          >
            <b-form-group
              label="Approved From"
              class="mb-md-0"
            >
              <datepicker
                v-model="filters.approved_at_from"
                :calendar-button="true"
                :clear-button="true"
                :bootstrap-styling="true"
                placeholder="YYYY-MM-DD"
              />
            </b-form-group>
          </b-col>

          <b-col
            md="2"
            cols="12"
            class="mb-2 mb-md-0"
          >
            <b-form-group
              label="Approved To"
              class="mb-md-0"
            >
              <datepicker
                v-model="filters.approved_at_to"
                :calendar-button="true"
                :clear-button="true"
                :bootstrap-styling="true"
                placeholder="YYYY-MM-DD"
              />
            </b-form-group>
          </b-col>

          <b-col
            md="2"
            cols="12"
            class="mb-2 mb-md-0"
          >
            <b-form-group
              :label="$t('clinic.doctors')"
              class="mb-md-0"
            >
              <v-select
                v-model="filters.doctor_ids"
                :options="doctorOptions"
                :disabled="loadingDoctors"
                multiple
                label="name"
                :reduce="(d) => d.id"
                :close-on-select="false"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :placeholder="$t('accounting.doctors-revenue-doctors-placeholder')"
              />
            </b-form-group>
          </b-col>

          <b-col
            md="2"
            cols="12"
            class="d-flex align-items-end"
          >
            <div
              class="mb-2 d-flex flex-wrap"
              style="gap: 0.5rem"
            >
              <b-button
                variant="primary"
                :disabled="loading"
                @click="applyFilters"
              >
                <b-spinner
                  v-if="loading"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="SearchIcon"
                  size="14"
                  class="mr-25"
                />
                {{ $t('common.search') }}
              </b-button>
              <b-button
                variant="outline-secondary"
                :disabled="loading"
                @click="resetFilters"
              >
                <feather-icon
                  icon="XIcon"
                  size="14"
                  class="mr-25"
                />
                {{ $t('common.reset') }}
              </b-button>
            </div>
          </b-col>
        </b-row>

        <div
          v-if="activeFilterLabel"
          class="active-filter-pill mt-2"
        >
          <feather-icon
            icon="FilterIcon"
            size="12"
            class="mr-25"
          />
          {{ activeFilterLabel }}
        </div>
      </b-card-text>
    </b-card>

    <b-card
      v-if="totals"
      class="mb-2"
    >
      <b-card-text>
        <b-row>
          <b-col
            md="3"
            lg="2"
            cols="6"
            class="mb-2"
          >
            <div class="border rounded p-2 text-center h-100">
              <small class="text-muted d-block">{{ $t('accounting.doctors-revenue-doctors-count') }}</small>
              <strong class="d-block mt-50">{{ totals.doctors_count }}</strong>
            </div>
          </b-col>
          <b-col
            md="3"
            lg="2"
            cols="6"
            class="mb-2"
          >
            <div class="border rounded p-2 text-center h-100">
              <small class="text-muted d-block">Total Invoices</small>
              <strong class="d-block mt-50">{{ totals.total_invoices || 0 }}</strong>
            </div>
          </b-col>
          <b-col
            md="3"
            lg="2"
            cols="6"
            class="mb-2"
          >
            <div class="border rounded p-2 text-center h-100">
              <small class="text-muted d-block">Total Amount</small>
              <strong class="d-block mt-50">{{ formatIQDCurrency(totals.total_amount) }}</strong>
            </div>
          </b-col>
          <b-col
            md="3"
            lg="2"
            cols="6"
            class="mb-2"
          >
            <div class="border rounded p-2 text-center h-100">
              <small class="text-muted d-block">Total Discount</small>
              <strong class="d-block mt-50">{{ formatIQDCurrency(totals.total_discount) }}</strong>
            </div>
          </b-col>
          <b-col
            md="3"
            lg="2"
            cols="6"
            class="mb-2"
          >
            <div class="border rounded p-2 text-center h-100">
              <small class="text-muted d-block">{{ $t('accounting.doctors-revenue-doctor-earnings') }}</small>
              <strong class="d-block mt-50 text-success">{{ formatIQDCurrency(totals.total_doctor_earnings) }}</strong>
            </div>
          </b-col>
          <b-col
            md="3"
            lg="2"
            cols="6"
            class="mb-2"
          >
            <div class="border rounded p-2 text-center h-100">
              <small class="text-muted d-block">{{ $t('accounting.doctors-revenue-doctor-received') }}</small>
              <strong class="d-block mt-50 text-info">{{ formatIQDCurrency(totals.total_doctor_received) }}</strong>
            </div>
          </b-col>
          <b-col
            md="3"
            lg="2"
            cols="6"
            class="mb-2"
          >
            <div class="border rounded p-2 text-center h-100">
              <small class="text-muted d-block">{{ $t('accounting.doctors-revenue-doctor-due') }}</small>
              <strong class="d-block mt-50 text-warning">{{ formatIQDCurrency(totals.total_doctor_due) }}</strong>
            </div>
          </b-col>
          <b-col
            md="3"
            lg="2"
            cols="6"
            class="mb-2"
          >
            <div class="border rounded p-2 text-center h-100">
              <small class="text-muted d-block">{{ $t('accounting.doctors-revenue-comprehensive-debt') }}</small>
              <strong class="d-block mt-50">{{ formatIQDCurrency(totals.comprehensive_debt) }}</strong>
            </div>
          </b-col>
        </b-row>
      </b-card-text>
    </b-card>

    <b-card>
      <b-card-text>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h3 class="mb-0">
            {{ $t('accounting.reports-doctors-revenue') }}
          </h3>
        </div>

        <b-table
          :items="doctorRows"
          :fields="tableColumns"
          :busy="loading"
          small
          responsive
          striped
          hover
          show-empty
          :empty-text="$t('common.no-data')"
        >
          <template #table-busy>
            <div class="text-center my-2">
              <b-spinner
                small
                class="mr-50"
              />
              <span>{{ $t('common.loading') }}</span>
            </div>
          </template>

          <template #cell(doctor_earnings)="data">
            <span class="font-weight-bold text-success">{{ formatIQDCurrency(data.item.doctor_earnings) }}</span>
          </template>
          <template #cell(total_amount)="data">
            <span class="font-weight-bold">{{ formatIQDCurrency(data.item.total_amount) }}</span>
          </template>
          <template #cell(total_discount)="data">
            <span class="font-weight-bold">{{ formatIQDCurrency(data.item.total_discount) }}</span>
          </template>
          <template #cell(doctor_received)="data">
            <span class="font-weight-bold text-info">{{ formatIQDCurrency(data.item.doctor_received) }}</span>
          </template>
          <template #cell(doctor_due)="data">
            <span class="font-weight-bold text-warning">{{ formatIQDCurrency(data.item.doctor_due) }}</span>
          </template>
          <template #cell(comprehensive_debt)="data">
            <b-badge variant="secondary">
              {{ formatIQDCurrency(data.item.comprehensive_debt) }}
            </b-badge>
          </template>
          <template #cell(actions)="data">
            <b-button
              size="sm"
              variant="outline-primary"
              @click="openDoctorDetails(data.item)"
            >
              Details
            </b-button>
          </template>
        </b-table>

        <small
          v-if="doctorRows.length"
          class="text-muted"
        >
          {{ $t('accounting.doctors-revenue-table-footer', { count: doctorRows.length }) }}
        </small>
      </b-card-text>
    </b-card>

    <b-modal
      v-model="showDoctorDetailsModal"
      size="xl"
      scrollable
      :title="selectedDoctor ? `${selectedDoctor.doctor_name} Details` : 'Doctor Details'"
      hide-footer
    >
      <div v-if="selectedDoctor">
        <b-row class="mb-2">
          <b-col
            md="3"
            cols="6"
            class="mb-1"
          >
            <small class="text-muted d-block">Invoices</small>
            <strong>{{ selectedDoctor.invoices_count || 0 }}</strong>
          </b-col>
          <b-col
            md="3"
            cols="6"
            class="mb-1"
          >
            <small class="text-muted d-block">Total Amount</small>
            <strong>{{ formatIQDCurrency(selectedDoctor.total_amount) }}</strong>
          </b-col>
          <b-col
            md="3"
            cols="6"
            class="mb-1"
          >
            <small class="text-muted d-block">Doctor Earnings</small>
            <strong class="text-success">{{ formatIQDCurrency(selectedDoctor.doctor_earnings) }}</strong>
          </b-col>
          <b-col
            md="3"
            cols="6"
            class="mb-1"
          >
            <small class="text-muted d-block">Doctor Due</small>
            <strong class="text-warning">{{ formatIQDCurrency(selectedDoctor.doctor_due) }}</strong>
          </b-col>
        </b-row>

        <b-table
          :items="selectedDoctorInvoices"
          :fields="invoiceColumns"
          small
          responsive
          striped
          hover
          show-empty
          :empty-text="$t('common.no-data')"
        >
          <template #cell(invoice_date)="data">
            <span>{{ formatDateTime(data.item.invoice_date) }}</span>
          </template>
          <template #cell(approved_date)="data">
            <span>{{ formatDateTime(data.item.approved_date) }}</span>
          </template>
          <template #cell(paid_date)="data">
            <span>{{ formatDateTime(data.item.paid_date) }}</span>
          </template>
          <template #cell(amount)="data">
            <span class="font-weight-bold">{{ formatIQDCurrency(data.item.amount) }}</span>
          </template>
          <template #cell(discount)="data">
            <span>{{ formatIQDCurrency(data.item.discount) }}</span>
          </template>
          <template #cell(doctor_amount)="data">
            <span class="text-success">{{ formatIQDCurrency(data.item.doctor_amount) }}</span>
          </template>
          <template #cell(hospital_amount)="data">
            <span>{{ formatIQDCurrency(data.item.hospital_amount) }}</span>
          </template>
          <template #cell(status)="data">
            <b-badge :variant="getStatusVariant(data.item.status)">
              {{ data.item.status || '-' }}
            </b-badge>
          </template>
          <template #cell(is_paid)="data">
            <b-badge :variant="data.item.is_paid ? 'success' : 'warning'">
              {{ data.item.is_paid ? 'Paid' : 'Unpaid' }}
            </b-badge>
          </template>
        </b-table>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardText,
  BRow,
  BCol,
  BFormGroup,
  BButton,
  BTable,
  BSpinner,
  BBadge,
  BModal,
} from 'bootstrap-vue'
import Datepicker from 'vuejs-datepicker'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BCardText,
    BRow,
    BCol,
    BFormGroup,
    BButton,
    BTable,
    BSpinner,
    BBadge,
    BModal,
    Datepicker,
    vSelect,
  },
  data() {
    return {
      filters: {
        paid_at_from: null,
        paid_at_to: null,
        approved_at_from: null,
        approved_at_to: null,
        doctor_ids: [],
      },
      doctorOptions: [],
      loading: false,
      loadingDoctors: false,
      reportData: null,
      showDoctorDetailsModal: false,
      selectedDoctor: null,
      tableColumns: [
        { key: 'doctor_name', label: this.$t('accounting.doctors-revenue-doctor-name'), sortable: true },
        { key: 'invoices_count', label: 'Invoices', sortable: true },
        { key: 'total_amount', label: 'Total Amount', sortable: true },
        { key: 'total_discount', label: 'Discount', sortable: true },
        { key: 'doctor_earnings', label: this.$t('accounting.doctors-revenue-doctor-earnings'), sortable: true },
        { key: 'doctor_received', label: this.$t('accounting.doctors-revenue-doctor-received'), sortable: true },
        { key: 'doctor_due', label: this.$t('accounting.doctors-revenue-doctor-due'), sortable: true },
        { key: 'comprehensive_debt', label: this.$t('accounting.doctors-revenue-comprehensive-debt'), sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
      invoiceColumns: [
        // { key: 'invoice_id', label: 'Invoice ID' },
        // { key: 'main_invoice_id', label: 'Main Invoice' },
        { key: 'invoice_type', label: 'Type' },
        { key: 'patient_name', label: 'Patient' },
        { key: 'invoice_date', label: 'Invoice Date' },
        { key: 'approved_date', label: 'Approved Date' },
        { key: 'paid_date', label: 'Paid Date' },
        { key: 'status', label: 'Status' },
        { key: 'amount', label: 'Amount' },
        { key: 'discount', label: 'Discount' },
        { key: 'doctor_amount', label: 'Doctor Amount' },
        { key: 'hospital_amount', label: 'Hospital Amount' },
        { key: 'is_paid', label: 'Paid' },
      ],
    }
  },
  computed: {
    doctorRows() {
      if (!this.reportData || !Array.isArray(this.reportData.doctors)) return []
      return this.reportData.doctors
    },
    totals() {
      return this.reportData?.totals || null
    },
    selectedDoctorInvoices() {
      return Array.isArray(this.selectedDoctor?.invoices) ? this.selectedDoctor.invoices : []
    },
    activeFilterLabel() {
      const parts = []
      const paidFrom = this.filters.paid_at_from ? this.formatDateForQuery(this.filters.paid_at_from) : null
      const paidTo = this.filters.paid_at_to ? this.formatDateForQuery(this.filters.paid_at_to) : null
      if (paidFrom && paidTo) parts.push(`Paid: ${paidFrom} → ${paidTo}`)
      else if (paidFrom) parts.push(`Paid ${this.$t('common.from-date')}: ${paidFrom}`)
      else if (paidTo) parts.push(`Paid ${this.$t('common.to-date')}: ${paidTo}`)

      const approvedFrom = this.filters.approved_at_from ? this.formatDateForQuery(this.filters.approved_at_from) : null
      const approvedTo = this.filters.approved_at_to ? this.formatDateForQuery(this.filters.approved_at_to) : null
      if (approvedFrom && approvedTo) parts.push(`Approved: ${approvedFrom} → ${approvedTo}`)
      else if (approvedFrom) parts.push(`Approved ${this.$t('common.from-date')}: ${approvedFrom}`)
      else if (approvedTo) parts.push(`Approved ${this.$t('common.to-date')}: ${approvedTo}`)

      const n = Array.isArray(this.filters.doctor_ids) ? this.filters.doctor_ids.length : 0
      if (n > 0) {
        parts.push(this.$t('accounting.doctors-revenue-n-doctors-selected', { n }))
      }
      return parts.length ? parts.join(' · ') : null
    },
  },
  async created() {
    await this.loadDoctorOptions()
    this.getReportData()
  },
  methods: {
    applyFilters() {
      this.getReportData()
    },
    resetFilters() {
      this.filters.paid_at_from = null
      this.filters.paid_at_to = null
      this.filters.approved_at_from = null
      this.filters.approved_at_to = null
      this.filters.doctor_ids = []
      this.getReportData()
    },
    buildQueryString() {
      const params = []
      const add = (k, v) => {
        if (v !== null && v !== undefined && v !== '') {
          params.push(`${k}=${encodeURIComponent(v)}`)
        }
      }
      add('paid_at_from', this.formatDateForQuery(this.filters.paid_at_from))
      add('paid_at_to', this.formatDateForQuery(this.filters.paid_at_to))
      add('approved_at_from', this.formatDateForQuery(this.filters.approved_at_from))
      add('approved_at_to', this.formatDateForQuery(this.filters.approved_at_to))
      const ids = Array.isArray(this.filters.doctor_ids) ? this.filters.doctor_ids.filter(Boolean) : []
      ids.forEach(id => {
        params.push(`doctor_ids[]=${encodeURIComponent(id)}`)
      })
      return params.length ? `?${params.join('&')}` : ''
    },
    formatDateForQuery(val) {
      if (!val) return ''
      if (typeof val === 'string') return val
      const d = new Date(val)
      if (Number.isNaN(d.getTime())) return ''
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    formatIQDCurrency(value) {
      const num = value === null || value === undefined ? 0 : Number(value)
      if (Number.isNaN(num)) return '0 IQD'
      return `${new Intl.NumberFormat('en-US').format(num)} IQD`
    },
    formatDateTime(value) {
      if (!value) return '-'
      return value
    },
    getStatusVariant(status) {
      if (!status) return 'secondary'
      if (status.includes('approved')) return 'success'
      if (status.includes('pending')) return 'warning'
      if (status.includes('cancel') || status.includes('reject')) return 'danger'
      return 'info'
    },
    openDoctorDetails(doctor) {
      this.selectedDoctor = doctor || null
      this.showDoctorDetailsModal = true
    },
    async loadDoctorOptions() {
      this.loadingDoctors = true
      try {
        const response = await this.$http.get('/doctor?take=500&sort=name')
        const rows = response?.data?.data
        if (Array.isArray(rows)) {
          this.doctorOptions = rows.map(d => ({
            id: d.id,
            name: d.name,
            type: d.type,
          }))
        } else {
          this.doctorOptions = []
        }
      } catch (e) {
        this.doctorOptions = []
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            text: e?.response?.data?.message || this.$t('common.error-fetching-data'),
            variant: 'danger',
          },
        })
      } finally {
        this.loadingDoctors = false
      }
    },
    async getReportData() {
      this.loading = true
      try {
        const url = `/reports/doctors-revenue${this.buildQueryString()}`
        const response = await this.$http.get(url)
        const payload = response?.data?.data

        if (!response?.data?.status) {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('common.error'),
              text:
                response?.data?.message
                || response?.data?.error
                || this.$t('common.error-fetching-data'),
              variant: 'danger',
            },
          })
        }

        this.reportData = payload || null
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            text:
              error?.response?.data?.message
              || error?.response?.data?.error
              || this.$t('common.error-fetching-data'),
            variant: 'danger',
          },
        })
        this.reportData = null
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.active-filter-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: rgba(115, 103, 240, 0.12);
  color: #7367f0;
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 600;
}
</style>
